import { format, parseISO } from 'date-fns';

export const formatCurrency = (amount: number | string | undefined | null, currency: string = 'USD'): string => {
  const symbols: Record<string, string> = {
    NGN: '₦',
    GHS: '₵',
    USD: '$',
    GBP: '£',
    CAD: 'C$',
  };

  const symbol = symbols[currency] || currency;
  const numAmount = amount == null ? 0 : (typeof amount === 'string' ? parseFloat(amount) : amount);
  return `${symbol}${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatDate = (date: string | Date, formatStr: string = 'MMM dd, yyyy'): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    // parseISO handles UTC strings and Date object displays in local time automatically
    return format(dateObj, formatStr);
  } catch (error) {
    return 'Invalid date';
  }
};

export const formatDateTime = (date: string | Date): string => {
  return formatDate(date, 'MMM dd, yyyy HH:mm');
};

// Convert local datetime-local input value to UTC ISO string for API
export const localDateTimeToUTC = (localDateTimeString: string): string => {
  if (!localDateTimeString) return '';
  
  // datetime-local format: "2024-02-18T14:30"
  // Browser treats this as local time
  const localDate = new Date(localDateTimeString);
  
  // Convert to UTC ISO string
  return localDate.toISOString();
};

// Convert UTC ISO string from API to local datetime-local format for input
export const utcToLocalDateTime = (utcISOString: string): string => {
  if (!utcISOString) return '';
  
  const date = new Date(utcISOString);
  
  // Format as "YYYY-MM-DDTHH:mm" for datetime-local input
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Convert local time input (HH:mm) to UTC time string
export const localTimeToUTC = (localTimeString: string): string => {
  if (!localTimeString) return '';
  
  // Use today's date as reference for timezone calculation
  const [hours, minutes] = localTimeString.split(':').map(Number);
  const localDate = new Date();
  localDate.setHours(hours, minutes, 0, 0);
  
  // Get UTC hours and minutes
  const utcHours = String(localDate.getUTCHours()).padStart(2, '0');
  const utcMinutes = String(localDate.getUTCMinutes()).padStart(2, '0');
  
  return `${utcHours}:${utcMinutes}`;
};

// Convert UTC time string (HH:mm) to local time
export const utcTimeToLocal = (utcTimeString: string): string => {
  if (!utcTimeString) return '';
  
  // Use today's date as reference for timezone calculation
  const [hours, minutes] = utcTimeString.split(':').map(Number);
  const utcDate = new Date();
  utcDate.setUTCHours(hours, minutes, 0, 0);
  
  // Get local hours and minutes
  const localHours = String(utcDate.getHours()).padStart(2, '0');
  const localMinutes = String(utcDate.getMinutes()).padStart(2, '0');
  
  return `${localHours}:${localMinutes}`;
};

export const formatPercentage = (value: number | string): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return `${numValue.toFixed(2)}%`;
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};
